import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import NotFound from "./pages/not-found";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import HomeTheaterSystems from "./pages/services/HomeTheaterSystems";
import SoundSystems from "./pages/services/SoundSystems";
import CommercialAVSolutions from "./pages/services/CommercialAVSolutions";
import ResidentialWiFiSurveillance from "./pages/services/ResidentialWiFiSurveillance";
import ClusterPage from "./pages/areas/ClusterPage";
import { CLUSTERS, SERVICES } from "./lib/areas";
import { pagePath, pageUrl } from "./lib/routes";
import { supplierImg } from "./lib/supplierImages";
import type { StripItem } from "./components/ImageStrip";

const sup = (n: string, alt: string): StripItem => ({ kind: "supplier", img: supplierImg(n), alt });
const main = (file: string, alt: string): StripItem => ({ kind: "main", file, alt });
const clusterCities = (slug: string) => {
  const cluster = CLUSTERS.find((entry) => entry.slug === slug);
  if (!cluster) throw new Error(`Unknown service area: ${slug}`);
  return cluster.cities;
};

const CLUSTER_IMAGERY: Record<
  string,
  {
    primaryImageBase: string;
    primaryImageAlt: string;
    stripItems: StripItem[];
    projectImageBase?: string;
    projectAlt?: string;
    projectHeading?: string;
    projectBody?: string;
    projectResponsiveImage?: boolean;
  }
> = {
  "leicester-area": {
    primaryImageBase: "j-s-audio-visual-solutions-living-room-av-installation",
    primaryImageAlt: "Living room audio visual installation in Leicester, MA by J&S Audio Video Solutions",
    stripItems: [
      main("J_S_home_theater_system_install_charlton_ma.jpg", "Home theater system installation in Charlton, MA by J&S Audio Video Solutions"),
      main("J_S_outdoor_bar_TV_installation_oakham_ma.jpg", "Outdoor TV installation over a stone bar and outdoor kitchen in Oakham, MA by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-tv-mounting-above-fireplace", "Television mounted above a fireplace in the Leicester area by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-outdoor-landscape-speaker-installation", "Outdoor landscape speaker installation in the Leicester area by J&S Audio Video Solutions"),
    ],
  },
  "worcester-area": {
    primaryImageBase: "j-s-audio-visual-solutions-living-room-tv-wall-mount",
    primaryImageAlt: "Living room television wall mount in Worcester, MA by J&S Audio Video Solutions",
    stripItems: [
      main("490522160_1172513571552469_2319196569004136287_n-shrewsbury.jpg", "Audio visual installation in Shrewsbury, MA by J&S Audio Video Solutions"),
      main("J_S_home_theater_fireplace_worcester_ma.jpg", "Television mounted above a fireplace in Worcester, MA by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-bar-audio-visual-installation-service", "Bar audio visual installation in Worcester, MA by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-commercial-av-installation-gym", "Commercial AV installation at a gym in the Worcester area by J&S Audio Video Solutions"),
    ],
  },
  "brookfield-area": {
    primaryImageBase: "j-s-audio-visual-solutions-home-living-room-av-installation",
    primaryImageAlt: "Home living room AV installation in East Brookfield, MA by J&S Audio Video Solutions",
    stripItems: [
      sup("j-s-audio-visual-solutions-home-entertainment-system-installation", "Home entertainment system installation in the Brookfield area by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-residential-av-installation-stone-wall", "Residential AV installation on a stone wall in the Brookfield area by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-outdoor-fireplace-entertainment-setup", "Outdoor fireplace entertainment setup in the Brookfield area by J&S Audio Video Solutions"),
    ],
  },
  "sturbridge-area": {
    primaryImageBase: "j-s-audio-visual-solutions-tv-mounting-above-fireplace",
    primaryImageAlt: "Television mounted above a fireplace in Sturbridge, MA by J&S Audio Video Solutions",
    stripItems: [
      sup("j-s-audio-visual-solutions-bar-tv-installation-service", "Bar television installation in the Sturbridge area by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-outdoor-landscape-lighting-installation", "Outdoor landscape lighting installation in the Sturbridge area by J&S Audio Video Solutions"),
      main("471501502_1058531619381024_1699632199117910748_n-restaurant.jpg", "Restaurant audio visual installation in the Sturbridge area by J&S Audio Video Solutions"),
    ],
  },
  "webster-area": {
    primaryImageBase: "j-s-audio-visual-solutions-home-entertainment-setup-furniture",
    primaryImageAlt: "Home entertainment setup with furniture in Webster, MA by J&S Audio Video Solutions",
    stripItems: [
      sup("j-s-audio-visual-solutions-home-tv-setup-cabinetry", "Home television setup integrated into cabinetry in the Webster area by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-bedroom-tv-wall-mount-installation", "Bedroom television wall mount installation in the Webster area by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-tv-and-soundbar-av-installation", "Television and soundbar AV installation in the Webster area by J&S Audio Video Solutions"),
    ],
  },
  "westborough-area": {
    primaryImageBase: "j-s-audio-visual-solutions-residential-av-system-installation",
    primaryImageAlt: "Residential audio visual system installation in Westborough, MA by J&S Audio Video Solutions",
    stripItems: [
      sup("j-s-audio-visual-solutions-modern-office-av-installation", "Modern office AV installation in the Westborough area by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-residential-home-entertainment-shelving", "Residential home entertainment with shelving in Northborough, MA by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-outdoor-speaker-deck-av-installation", "Outdoor deck speaker installation in the Westborough area by J&S Audio Video Solutions"),
    ],
    projectImageBase: "westborough-home-theater-after-1",
    projectAlt: "Television installed in built-in cabinetry in a Westborough, MA living room",
    projectHeading: "Television installation in Westborough built-ins.",
    projectBody: "The finished view shows the television installed within the room's existing built-in cabinetry.",
    projectResponsiveImage: true,
  },
  "marlborough-area": {
    primaryImageBase: "j-s-audio-visual-solutions-home-av-installation-display",
    primaryImageAlt: "Home audio visual installation in Marlborough, MA by J&S Audio Video Solutions",
    stripItems: [
      main("J_S_home_theater_system_install_hopkinton_ma.jpg", "Home theater system installation in Hopkinton, MA by J&S Audio Video Solutions"),
      main("TV_soundbar_Southboro.jpg", "Television and soundbar installation in Southborough, MA by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-network-audio-visual-equipment", "Network audio visual equipment rack in the Marlborough area by J&S Audio Video Solutions"),
    ],
  },
  "milford-area": {
    primaryImageBase: "j-s-audio-visual-solutions-residential-av-display-installation",
    primaryImageAlt: "Residential audio visual display installation in Milford, MA by J&S Audio Video Solutions",
    stripItems: [
      {
        kind: "supplier",
        img: supplierImg("uxbridge-home-theater-system"),
        alt: "Wall-mounted television with speakers and an AV cabinet in Uxbridge, MA",
        caption: "Uxbridge, MA",
        responsiveVariantByViewport: true,
      },
      main("J_S_TV_installation_services_mendon.jpg", "Television installation in Mendon, MA by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-living-room-tv-installation", "Living room television installation in the Milford area by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-ceiling-mounted-speaker-installation", "Ceiling-mounted speaker installation in the Milford area by J&S Audio Video Solutions"),
    ],
  },
  "framingham-area": {
    primaryImageBase: "j-s-audio-visual-solutions-modern-office-av-installation",
    primaryImageAlt: "Office AV installation in the Framingham area by J&S Audio Video Solutions",
    stripItems: [
      sup("j-s-audio-visual-solutions-home-theater-fireplace-soundbar-sherborn", "Television and soundbar mounted above a fireplace in Sherborn, MA by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-office-ceiling-speaker-installation", "Office ceiling speaker installation in the Framingham area by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-network-audio-visual-equipment", "Network audio visual equipment rack in the Framingham area by J&S Audio Video Solutions"),
      sup("j-s-audio-visual-solutions-bar-tv-installation-service", "Bar television installation in the Framingham area by J&S Audio Video Solutions"),
    ],
  },
};

const INDEXABLE_PATHS = new Set([
  "/",
  pagePath("/contact"),
  pagePath("/privacy"),
  ...SERVICES.map((service) => service.path),
  ...CLUSTERS.map((cluster) => cluster.path),
]);

const CanonicalTag = () => {
  const location = useLocation();
  const pathname = pagePath(location.pathname);
  const canonicalUrl = INDEXABLE_PATHS.has(pathname) ? pageUrl(pathname) : null;

  useEffect(() => {
    const existing = Array.from(document.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]'));
    if (!canonicalUrl) {
      existing.forEach((link) => link.remove());
      return;
    }
    const canonical = document.getElementById("route-canonical") as HTMLLinkElement | null
      ?? existing[0]
      ?? document.createElement("link");
    canonical.id = "route-canonical";
    canonical.rel = "canonical";
    canonical.href = canonicalUrl;
    if (!canonical.isConnected) document.head.appendChild(canonical);
    existing.forEach((link) => {
      if (link !== canonical) link.remove();
    });
  }, [canonicalUrl]);

  if (!canonicalUrl) return null;
  return (
    <Helmet>
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content="https://jandsav.com/og-jands-home-theater-20261009.png" />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="J&S Audio Video Solutions" />
    </Helmet>
  );
};

export function AppRoutes() {
  return (
    <>
      <CanonicalTag />
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="about-us" element={<Navigate to={{ pathname: "/", hash: "#about" }} replace />} />
            <Route path="services" element={<Navigate to={{ pathname: "/", hash: "#services" }} replace />} />
            <Route path="services/home-theater-systems" element={<HomeTheaterSystems />} />
            <Route path="services/sound-systems" element={<SoundSystems />} />
            <Route path="services/commercial-av-solutions" element={<CommercialAVSolutions />} />
            <Route path="services/residential-wifi-surveillance" element={<ResidentialWiFiSurveillance />} />
            
            {/* Cluster Pages */}
            <Route path="areas/leicester-area" element={<ClusterPage clusterName="Leicester Area" cities={["Leicester", "Spencer", "Paxton", "Rutland", "Oakham", "Charlton"]} {...CLUSTER_IMAGERY["leicester-area"]} projectImageBase="j-s-audio-visual-solutions-outdoor-patio-tv-sound-system-leicester" projectAlt="Outdoor patio television and sound system on a stone patio in Leicester, MA by J&S Audio Video Solutions" projectHeading="An outdoor patio in Leicester built for game day and quiet evenings." projectBody="A Leicester family wanted their stone patio to work as hard as the living room. We mounted a weather-rated television on the retaining wall so it stays watchable in daylight, and tied in outdoor speakers tuned to carry music across the seating area without pushing it into the neighbor's yard. Everything runs from one app, so they can bring up the game or background music with a single tap, and the gear is rated to ride out a New England winter." />} />
            <Route path="areas/worcester-area" element={<ClusterPage clusterName="Worcester Area" cities={["Worcester", "Auburn", "Holden", "Shrewsbury", "West Boylston"]} {...CLUSTER_IMAGERY["worcester-area"]} projectImageBase="j-s-audio-visual-solutions-home-theater-fireplace-built-ins-shrewsbury" projectAlt="Large television integrated into custom fireplace built-ins in Shrewsbury, MA by J&S Audio Video Solutions" projectHeading="A Shrewsbury living room where the TV fits the millwork, not the other way around." projectBody="A Shrewsbury homeowner had beautiful custom built-ins flanking the fireplace and did not want a black rectangle fighting the woodwork. We sized and mounted the display to sit cleanly within the millwork, hid the soundbar and cabling so nothing breaks the lines, and set the picture height for comfortable viewing from the couch. The result reads as part of the room, with the surround audio doing the heavy lifting you cannot see." />} />
            <Route path="areas/brookfield-area" element={<ClusterPage clusterName="Brookfield Area" cities={["East Brookfield", "Brookfield"]} {...CLUSTER_IMAGERY["brookfield-area"]} />} />
            <Route path="areas/sturbridge-area" element={<ClusterPage clusterName="Sturbridge Area" cities={["Sturbridge", "Southbridge", "Brimfield"]} {...CLUSTER_IMAGERY["sturbridge-area"]} />} />
            <Route path="areas/webster-area" element={<ClusterPage clusterName="Webster Area" cities={["Webster", "Oxford", "Dudley", "Thompson (CT)"]} {...CLUSTER_IMAGERY["webster-area"]} projectImageBase="j-s-audio-video-solutions-dudley-ceiling-speakers-2" projectAlt="In-ceiling speakers set among skylights and pendant lights in a Dudley, MA home by J&S Audio Video Solutions" projectHeading="In-ceiling audio for a Dudley home." projectBody="This Dudley project brings sound into the living space without adding speaker cabinets to the walls or floor. J&S installed flush-mounted ceiling speakers among the recessed lights, skylights, and pendants, keeping the finished ceiling visually clean. The room-wide view shows how the speaker placement fits the architecture, while the detail photos on our Sound Systems page show the finished installation up close." />} />
            <Route path="areas/westborough-area" element={<ClusterPage clusterName="Westborough Area" cities={["Northborough", "Westborough", "Upton"]} {...CLUSTER_IMAGERY["westborough-area"]} />} />
            <Route path="areas/marlborough-area" element={<ClusterPage clusterName="Marlborough Area" cities={["Marlborough", "Southborough", "Hudson", "Hopkinton"]} {...CLUSTER_IMAGERY["marlborough-area"]} />} />
            <Route path="areas/milford-area" element={<ClusterPage clusterName="Milford Area" cities={clusterCities("milford-area")} {...CLUSTER_IMAGERY["milford-area"]} />} />
            <Route path="areas/framingham-area" element={<ClusterPage clusterName="Framingham Area" cities={["Framingham", "Natick", "Ashland", "Sherborn, MA"]} {...CLUSTER_IMAGERY["framingham-area"]} projectImageBase="j-s-audio-visual-solutions-metrowest-financial-conference-room" projectAlt="77 inch conference room displays and audio installed for a MetroWest financial firm by J&S Audio Video Solutions" projectHeading="A MetroWest financial firm got a room the whole team can present in." projectBody="A financial firm in the Framingham area needed a conference room that worked for the whole team, not just whoever sat at the front. We mounted 77 inch displays that anyone can cast to straight from a phone or laptop, so presentations go up in seconds without hunting for cables or adapters. We also reworked the room audio so a large group can clearly hear both the presenter and the sound from any slideshow, replacing the thin built-in speakers with a setup tuned to the space." />} />

            <Route path="contact" element={<Contact />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
    </>
  );
}

function App() {
  const rawBase = import.meta.env.BASE_URL.replace(/\/$/, "");
  const basename = rawBase === "" ? "/" : rawBase;

  return (
    <HelmetProvider>
      <BrowserRouter basename={basename}>
        <AppRoutes />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
